import { getAuthToken } from './auth'
import { showWarningToast } from '../utils/toastHelpers'
import { checkFeatureFlag } from '../utils/feature-flag-manager'
import { FeatureFlag } from '../feature-flags'
import { jwtDecode } from 'jwt-decode'

export const API_URL = process.env.REACT_APP_API_URL!
const API_KEY = process.env.REACT_APP_API_KEY

/**
 * Decodes a JWT token to extract user information
 * Uses jwt-decode library for reliable token decoding
 */
const decodeJWT = (token: string): { username?: string } | null => {
  try {
    const decoded = jwtDecode<{ username?: string }>(token)
    return decoded
  } catch (error) {
    console.warn('Failed to decode JWT token:', error)
    return null
  }
}

/**
 * Custom fetcher used by codegen'd hooks to first load auth token, then make the fetch request.
 * Modified to support partial data (errorPolicy: 'all') - returns data even when there are GraphQL errors
 * so that React Query can display partial results.
 */
export const fetcher = <TData, TVariables>(query: string, variables?: TVariables) => {
  return async (): Promise<TData> => {
    const authToken = await getAuthToken()
    const res = await fetch(API_URL, {
      method: 'POST',
      // ...{ credentials: 'include' },
      headers: {
        ...(authToken ? { Authorization: authToken } : API_KEY ? { 'x-api-key': API_KEY } : {}),
      },
      body: JSON.stringify({ query, variables }),
    })

    const json = await res.json()

    // If there are errors but we also have data, return the data (partial data support)
    // This allows the UI to display what it can even when some fields fail to serialize
    if (json.errors && json.data) {
      // Log the errors for debugging
      console.warn('GraphQL errors encountered, but returning partial data:', json.errors)

      // Check feature flag to determine if we should show toast notification
      if (authToken && typeof authToken === 'string') {
        try {
          const decodedToken = decodeJWT(authToken)
          const userId = decodedToken?.username
          if (userId) {
            const shouldShowToast = await checkFeatureFlag(
              'operational-partial-data-toast-notification' as FeatureFlag,
              userId
            )

            if (shouldShowToast) {
              // Show a warning toast to inform the user that some data may be incomplete
              const errorMessages = json.errors
                .map((error: any) => error.message || 'Unknown error')
                .slice(0, 2) // Show first 2 errors to avoid overwhelming the user
                .join(', ')

              showWarningToast(
                'Something was wrong',
                `${errorMessages}${json.errors.length > 2 ? '...' : ''}`,
                { duration: 6000 }
              )
            }
          }
        } catch (error) {
          // If feature flag check fails, don't show toast (fail silently)
          console.warn('Failed to check feature flag for partial data toast:', error)
        }
      }

      return json.data
    }

    // If there are errors and no data, throw an error. errorType (e.g. Forbidden, CONFLICT) lets
    // a screen tell a refusal it can explain from an unexpected failure.
    if (json.errors) {
      const { message, errorType } = json.errors[0]
      throw Object.assign(new Error(message), errorType ? { errorType } : {})
    }

    return json.data
  }
}
